import { ModuleLabel } from '../module-label';
import '../../assets/user-details.css';
import { DataTable } from "primereact/datatable";
import {
    UserDto,
    StatsModuleDto,
    StatsSessionDto,
    GetUserStatsApiResponseDto,
    SessionDto,
    GetUserSessionsApiResponseDto,
    GetUserSessionEventsApiResponseDto,
    SessionEventDto
} from "../../general/dtos";
import { Column } from "primereact/column";
import { useContext, useEffect, useState } from 'react';
import { Api } from '../../general/api';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { AxiosError } from 'axios';
import { GeneralErrorDialog } from '../dialogs/error-dialog';
import { UserSessionExplorer } from './user-session-explorer';
import { UserManagementDataContext, UserManagementDataContextValue } from '../../providers/user-management-provider';
import { useApi } from '../../providers/api-provider';
import { Button } from 'primereact/button';
import { useCompactViewport } from '../../general/use-compact-viewport';

interface RecentModulesListProps {
    modules: StatsModuleDto[];
    loading: boolean;
}

export function RecentModulesList({ modules, loading }: RecentModulesListProps) {
  const catalog = useContext(UserManagementDataContext)?.allModules;
  return (
    <DataTable value={modules} size="small" scrollable scrollHeight="10rem" header="Recent modules" style={{ fontSize: "0.85rem" }} loading={loading}
        emptyMessage={<div className="flex align-items-center gap-2"><i className="pi pi-info-circle" /><span>No activity recorded</span></div>} >
      <Column field="module_name" header="Module" body={(row: StatsModuleDto | StatsSessionDto) => <ModuleLabel name={row.module_name} managed={catalog?.find(m => m.module_key === row.module_key)?.is_managed} />} />
      <Column field="last_used" header="Last accessed" bodyClassName="activity-timestamp" />
    </DataTable>
  );
}

interface RecentSessionsListProps {
    sessions: StatsSessionDto[];
    loading: boolean;
}

export function RecentSessionsList({ sessions, loading }: RecentSessionsListProps) {
  const catalog = useContext(UserManagementDataContext)?.allModules;
  return (
    <DataTable value={sessions} size="small" scrollable scrollHeight="10rem" header="Recent sessions" style={{ fontSize: "0.85rem" }} loading={loading}
        emptyMessage={<div className="flex align-items-center gap-2"><i className="pi pi-info-circle" /><span>No activity recorded</span></div>} >
      <Column field="module_name" header="Module" body={(row: StatsModuleDto | StatsSessionDto) => <ModuleLabel name={row.module_name} managed={catalog?.find(m => m.module_key === row.module_key)?.is_managed} />} />
      <Column field="started_at" header="Started" bodyClassName="activity-timestamp" />
      <Column field="finished_at" header="Finished" bodyClassName="activity-timestamp" />
    </DataTable>
  );
}

interface UserDetailsProps {
    selectedUsers: UserDto[];
}

export function UserDetails({ selectedUsers }: UserDetailsProps) {
  const compact = useCompactViewport();
  const [showRecentActivity, setShowRecentActivity] = useState(false);
  const ctx: UserManagementDataContextValue | null = useContext<UserManagementDataContextValue | null>(UserManagementDataContext);
  const { userStats, userStatsLoading, userStatsError } = ctx!;

  const api: Api = useApi();
  const [summary, setSummary] = useState<GetUserStatsApiResponseDto | null>(null);
  const [error, setError] = useState<string>('');
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorDetails, setErrorDetails] = useState<string>('');

  useEffect(() => {
    if (userStatsError) {
        setError(userStatsError);
        setErrorTitle('Error loading recent user activities');
        setErrorDetails('There was an unhandled error while trying to load the recent user activities. Please try again later or contact support :)');
    }
  }, [userStatsError]);

  const loadUserSessions = async (moduleKey: string): Promise<SessionDto[]> => {
    try {
        const json: GetUserSessionsApiResponseDto | null = await api.getUserSessions(selectedUsers[0].sub, moduleKey);
        return json?.data ?? [];
    } catch (error: AxiosError | any) {
        const message: string = error.response?.data?.message ?? 'Load error';
        setError(message);
        setErrorTitle('Error loading user sessions');
        setErrorDetails('There was an unhandled error while trying to load the user sessions. Please try again later or contact support :)');
    }
    return [];
  }

  const loadUserSessionEvents = async (sessionId: string): Promise<SessionEventDto[]> => {
    try {
        const json: GetUserSessionEventsApiResponseDto | null = await api.getUserSessionEvents(selectedUsers[0].sub, sessionId);
        return json?.data ?? [];
    } catch (error: AxiosError | any) {
        const message: string = error.response?.data?.message ?? 'Load error';
        setError(message);
        setErrorTitle('Error loading session events');
        setErrorDetails('There was an unhandled error while trying to load the user session events. Please try again later or contact support :)');
    }
    return [];
  }

  useEffect(() => {
    if (selectedUsers.length === 1 && !userStats[selectedUsers[0].sub]) {
        ctx?.loadUserStats(selectedUsers[0].sub)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers]);

  useEffect(() => {
    if (selectedUsers.length === 1) {
        const stats: GetUserStatsApiResponseDto = userStats[selectedUsers[0].sub];
        if (stats) {
            setSummary(stats);
        }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUsers, userStats]);

  if (selectedUsers.length === 0) {
    return <div style={{ padding: 16 }}>No user selected</div>;
  }

  if (selectedUsers.length > 1) {
    return <div style={{ padding: 16 }}>Multiple users selected</div>;
  }

  return (<>
    <div className="user-details-page">
        {compact && <Button type="button" text size="small" className="recent-activity-toggle"
            label="Recent activity" icon={showRecentActivity ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
            aria-expanded={showRecentActivity} aria-controls="user-recent-activity"
            onClick={() => setShowRecentActivity(shown => !shown)} />}
        <div id="user-recent-activity" className="activity-summary" role="region" aria-label="Recent activity summaries" hidden={compact && !showRecentActivity}>
            <div className="summary-panel">
                <RecentModulesList modules={summary?.recent_modules ?? []} loading={userStatsLoading} />
            </div>
            <div className="summary-panel">
                <RecentSessionsList sessions={summary?.recent_sessions ?? []} loading={userStatsLoading} />
            </div>
        </div>
        <div className="session-explorer-container">
            <h3 className="explorer-title">User sessions</h3>
            <UserSessionExplorer
                currentUser={selectedUsers[0]}
                loadSessions={loadUserSessions}
                loadEvents={loadUserSessionEvents}
            />
        </div>

        <GeneralErrorDialog
            visible={error.length > 0}
            title={errorTitle}
            message={error}
            details={errorDetails}
            onClose={() => setError('')}
        />
    </div>
  </>);
}
